import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking, Platform } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useAppSelector } from '@/store';
import { useResponsive } from '@/hooks/common/useResponsive';
import { getEntrataUrl } from '@/utils/residents/maintenance/entrataPropertyUrls';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { strings } from '@/constants/residents/strings';


type Variant = 'disabled' | 'future';

export const UndergoingMaintenancePage: React.FC<{ variant?: Variant }> = ({ variant = 'disabled' }) => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = (user as any)?.property?.id as number | undefined;
  const entrataUrl = getEntrataUrl(propertyId);

  const handleEntrataPress = () => {
    if (entrataUrl) {
      if (Platform.OS === 'web') {
        // Use globalThis to access window safely
        const win = (globalThis as any)?.window;
        if (win && win.open) {
          win.open(entrataUrl, '_blank');
        } else {
          Linking.openURL(entrataUrl);
        }
      } else {
        Linking.openURL(entrataUrl);
      }
    }
  };

  const mobileTitle = strings.maintenance.disabledTitle;
  const mobileMessage = variant === 'future' ? strings.maintenance.futureDescription : strings.maintenance.disabledDescription;

  const desktopTitle = strings.maintenance.disabledTitle;
  const desktopMessage = variant === 'future' ? strings.maintenance.futureDescription : strings.maintenance.disabledDescription;

  if (!isDesktop) {
    // Mobile view
    return (
      <View style={styles.mobileContainer}>
        <View style={styles.mobileIconContainer}>
          <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={64} height={64} />
        </View>
        <Text style={styles.mobileTitle}>{mobileTitle}</Text>
        <Text style={styles.mobileMessage}>{mobileMessage}</Text>
        {entrataUrl && (
          <TouchableOpacity style={styles.mobileLink} onPress={handleEntrataPress}>
            <Text style={styles.mobileLinkText}>{entrataUrl}</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  // Desktop view
  return (
    <View style={styles.desktopContainer}>
      <View style={styles.desktopIconContainer}>
        <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={120} height={120} />
      </View>
      <View style={styles.desktopContent}>
        <Text style={styles.desktopTitle}>{desktopTitle}</Text>
        <Text style={styles.desktopMessage}>{desktopMessage}</Text>
        {entrataUrl && (
          <TouchableOpacity style={styles.desktopLink} onPress={handleEntrataPress}>
            <Text style={styles.desktopLinkText}>{entrataUrl}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Mobile styles
  mobileContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  mobileIconContainer: {
    marginBottom: 32,
  },
  mobileTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 8,
  },
  mobileMessage: {
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    lineHeight: 20,
  },
  mobileLink: {
    paddingVertical: 8,
  },
  mobileLinkText: {
    fontSize: 14,
    color: '#3B82F6',
    textAlign: 'center',
    textDecorationLine: 'underline',
  },

  // Desktop styles
  desktopContainer: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    minHeight: '100%',
  },
  desktopIconContainer: {
    marginBottom: 32,
  },
  desktopContent: {
    alignItems: 'center',
    maxWidth: '80%',
    gap: 16,
  },
  desktopTitle: {
    fontSize: 32,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
  },
  desktopMessage: {
    fontSize: 18,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    lineHeight: 28,
    maxWidth: 600,
  },
  desktopLink: {
    paddingVertical: 12,
    marginTop: 8,
  },
  desktopLinkText: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    fontWeight: '600',
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
});
